import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import NoData from "@/components/desktop/no-data/NoData";
import ExpandableRow from "./ExpandableRow";
import CustomButton from "@/components/desktop/button/CustomButton";

const TableBody = ({
	styles,
	getTableProps,
	getTableBodyProps,
	page,
	prepareRow,
	renderSharedColGroup,
	onRowClick,
	shouldEnableExpander,
	renderSubComponent,
	visibleColumns,
	compactBody = false,
	useExpandableRows = false,
	renderExpandedContent,
	useRowspan = false,
	rowspanConfig,
}) => {
	const [expandedGroups, setExpandedGroups] = useState(new Set());

	const toggleGroup = (groupKey) => {
		setExpandedGroups((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(groupKey)) {
				newSet.delete(groupKey);
			} else {
				newSet.add(groupKey);
			}
			return newSet;
		});
	};
	const processedRows = useMemo(() => {
		if (!useRowspan || !rowspanConfig || page.length === 0) {
			return page.map((row) => ({ row, rowspans: {}, isLastInSubgroup: false, isLastInGroup: false, isFirstInGroup: true, groupKey: null }));
		}

		const { groupKey, rowspanColumns, subgroupKey } = rowspanConfig;
		const groups = new Map();
		const result = [];

		page.forEach((row) => {
			const key = groupKey(row.original);
			if (!groups.has(key)) {
				groups.set(key, []);
			}
			groups.get(key).push(row);
		});

		groups.forEach((groupRows, key) => {
			const rowspan = groupRows.length;
			groupRows.forEach((row, index) => {
				const rowspans = {};
				const isFirstInGroup = index === 0;
				const isLastInGroup = index === groupRows.length - 1;

				let isLastInSubgroup = false;
				if (subgroupKey) {
					const currentSubgroup = subgroupKey(row.original);
					if (currentSubgroup) {
						const pageIndex = page.findIndex(
							(r) =>
								r.original.id === row.original.id &&
								r.original.application_number === row.original.application_number &&
								r.original.product_id === row.original.product_id,
						);
						const nextRowInPage = page[pageIndex + 1];
						if (!nextRowInPage || subgroupKey(nextRowInPage.original) !== currentSubgroup) {
							isLastInSubgroup = true;
						}
					}
				}

				rowspanColumns.forEach((accessor) => {
					if (index === 0) {
						rowspans[accessor] = rowspan;
					} else {
						rowspans[accessor] = 0;
					}
				});
				result.push({ row, rowspans, isLastInSubgroup, isLastInGroup, isFirstInGroup, groupKey: key });
			});
		});

		return result;
	}, [page, useRowspan, rowspanConfig]);

	return (
		<table {...getTableProps()} className={styles.table} role="table">
			{renderSharedColGroup()}
			<tbody {...getTableBodyProps()} className={styles.tbody}>
				{processedRows.map(({ row, rowspans, isLastInSubgroup, isLastInGroup, isFirstInGroup, groupKey }) => {
					if (useExpandableRows && renderExpandedContent && !useRowspan) {
						return (
							<ExpandableRow
								key={row.id}
								row={row}
								renderExpandedContent={renderExpandedContent}
								colSpan={visibleColumns.length}
								prepareRow={prepareRow}
								onRowClick={onRowClick}
								compactBody={compactBody}
								styles={styles}
							/>
						);
					}

					prepareRow(row);
					const { key: rowKey, ...rowProps } = row.getRowProps();
					const fragmentKey = rowKey ?? `row-${row.id ?? row.index}`;
					const isGroupExpanded = expandedGroups.has(groupKey);

					const shouldShowSubgroupBorder =
						rowspanConfig?.showSubgroupBorder && isLastInSubgroup && row.original?.sub_group && !isFirstInGroup && !isLastInGroup;

					return (
						<React.Fragment key={fragmentKey}>
							<tr
								{...rowProps}
								className={`${styles.tr} ${isGroupExpanded ? styles.trExpanded : ""} ${onRowClick ? styles.trClickable : ""}`}
								onClick={onRowClick ? () => onRowClick(row) : undefined}
								style={{
									...rowProps.style,
									borderBottom: shouldShowSubgroupBorder ? "1px solid #000" : undefined,
								}}
							>
								{row.cells.map((cell) => {
									const columnAccessor = cell.column.id;
									const rowspan = rowspans[columnAccessor];

									if (rowspan === 0) return null;

									const { key, ...cellProps } = cell.getCellProps();
									const extraClass = cell.column?.meta?.cellClassName ? styles[cell.column.meta.cellClassName] : "";
									const compactClass = compactBody ? styles.tdCompact : "";
									const titleText = typeof cell.value === "string" ? cell.value : undefined;

									const isExpandColumn = columnAccessor === "_expand";
									const shouldShowExpandButton = useExpandableRows && renderExpandedContent && isExpandColumn && isFirstInGroup;

									if (isExpandColumn && shouldShowExpandButton) {
										return (
											<td
												key={key}
												{...cellProps}
												rowSpan={rowspan > 1 ? rowspan : undefined}
												className={`${styles.td} ${extraClass} ${compactClass}`}
											>
												<span className={styles.cellInner}>
													<CustomButton
														as="button"
														variant="ghost"
														size="sm"
														className={styles.expandButton}
														onClick={(e) => {
															e.stopPropagation();
															toggleGroup(groupKey);
														}}
														ariaLabel={isGroupExpanded ? "Contraer" : "Expandir"}
														title={isGroupExpanded ? "Contraer" : "Expandir"}
													>
														{isGroupExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
													</CustomButton>
												</span>
											</td>
										);
									}

									return (
										<td
											key={key}
											{...cellProps}
											rowSpan={rowspan > 1 ? rowspan : undefined}
											className={`${styles.td} ${extraClass} ${compactClass}`}
											title={titleText}
										>
											<span className={styles.cellInner}>{cell.render("Cell")}</span>
										</td>
									);
								})}
							</tr>
							{useExpandableRows && renderExpandedContent && isGroupExpanded && isLastInGroup && (
								<tr key={`${fragmentKey}-expanded`} className={styles.expandedRow}>
									<td colSpan={visibleColumns.length} className={styles.expandedCell}>
										{renderExpandedContent(row)}
									</td>
								</tr>
							)}
							{shouldEnableExpander && renderSubComponent && row.isExpanded && (
								<tr key={`${fragmentKey}-sub`} className={styles.subRow}>
									<td colSpan={visibleColumns.length} className={styles.subTd}>
										{renderSubComponent(row)}
									</td>
								</tr>
							)}
						</React.Fragment>
					);
				})}

				{processedRows.length === 0 && (
					<tr>
						<td className={styles.empty} colSpan={1000}>
							<NoData />
						</td>
					</tr>
				)}
			</tbody>
		</table>
	);
};

export default TableBody;
